import React, { useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCityPrice } from "../../hooks/useCityPrice";
import { isServiceEnabled } from "../../config";
import { CARETAKER_SERVICES } from "../../config/pricing";
import CategorySidebar from "./CategorySidebar";

import baby_caretaker_img    from "../../components/images/stage_01/baby_caretaker.png";
import baby_caretaker_169_img from "../../components/images/stage_01/baby_caretaker_169.png";
import elder_caretaker_img   from "../../components/images/stage_01/elder_caretaker_R2.png";
import elder_caretaker_169_img from "../../components/images/stage_01/elder_caretaker_169.png";
import patient_caretaker_img from "../../components/images/stage_01/patient_caretaker.png";
import patient_caretaker_169_img from "../../components/images/stage_01/patient_caretaker_169.png";
import medical_caretaker_img from "../../components/images/stage_01/medical_caretaker.png";
import caretaker_general_img from "../../components/images/stage_01/caretaker_service_general.png";

import home_services_img     from "../../components/images/stage_01/complete_house_cleaning.png";
import technical_services_img from "../../components/images/stage_01/electrical.png";
import outdoor_services_img  from "../../components/images/stage_01/gardening.png";
import transport_services_img from "../../components/images/stage_01/complete_packers_movers.png";
import special_services_img  from "../../components/images/stage_01/special_service.png";

// "What we do" on each Details modal is built straight from the same
// checklist options the actual requirement forms use — not a hand-typed
// summary that can drift out of sync with what the form really offers.
import { BABY_CARE_OPTIONS, SAFETY_OPTIONS } from "../workerspage/caretaker/BabyCaretaker";
import { ELDER_CARE_GROUPS } from "../workerspage/caretaker/ElderCare";
import { PATIENT_CARE_GROUPS } from "../workerspage/caretaker/PatientCare";
import { NURSING_OPTIONS } from "../workerspage/caretaker/HomeNursingSupport";
import { GENERAL_CARE_GROUPS } from "../workerspage/caretaker/GeneralCaretaker";

const fallbackImage =
  "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=900&q=80";

// Flatten a flat option list (baby/nursing) into label strings.
const flatLabels = (options) => options.map((o) => o.label);

// Flatten a grouped option list (elder/patient/general) into
// "Group – Label" strings, so the reader can see which category each
// item belongs to instead of one long undifferentiated list.
const groupedLabels = (groups) =>
  groups.flatMap((g) => g.options.map((o) => `${g.group} – ${o.label}`));


export const services = [
  {
    id: "cs-1",
    name: "Baby Care",
    desc: "Trained caretakers for newborn and toddler support at home.",
    ...CARETAKER_SERVICES["cs-1"],
    image: baby_caretaker_img,
    images: [baby_caretaker_img],
    color: "#ec4899",
    icon: "👶",
    link: "/service/caretaker-service/baby-care",
    // From BABY_CARE_OPTIONS + SAFETY_OPTIONS in BabyCaretaker.js — this
    // card covers the Baby/Infant/Toddler bucket of that form (its
    // School-going Child / Child Care checklist is a separate bucket,
    // not shown here).
    includes: [...flatLabels(BABY_CARE_OPTIONS), ...flatLabels(SAFETY_OPTIONS)],
    excludes: [
      "Medical treatment or injections",
      "Overnight stay unless booked separately",
      "Baby product purchase or grocery runs",
      "Handling sick or medically critical infants",
    ],
  },
  {
    id: "cs-2",
    name: "Elder Caretaker",
    desc: "Compassionate support for senior citizens with daily assistance and companionship.",
    ...CARETAKER_SERVICES["cs-2"],
    image: elder_caretaker_img,
    images: [elder_caretaker_img],
    color: "#8b5cf6",
    icon: "🧓",
    link: "/service/caretaker-service/elder-care",
    // From ELDER_CARE_GROUPS in ElderCare.js — all 6 groups (Personal
    // Care, Mobility, Daily Support, Companionship, Supervision, Outside
    // Support), the exact checklist the requirement form offers.
    includes: groupedLabels(ELDER_CARE_GROUPS),
    excludes: [
      "Medical diagnosis or treatment",
      "Physiotherapy or specialised therapy",
      "Housekeeping beyond the elder's own room",
      "Independently purchasing medications or groceries",
    ],
  },
  {
    id: "cs-3",
    name: "Patient Care",
    desc: "At-home patient assistance for recovery cases with daily care and mobility support.",
    ...CARETAKER_SERVICES["cs-3"],
    image: patient_caretaker_img,
    images: [patient_caretaker_img],
    color: "#14b8a6",
    icon: "🏥",
    link: "/service/caretaker-service/patient-care",
    // From PATIENT_CARE_GROUPS in PatientCare.js — all 4 groups. Vital
    // Monitoring / Nursing Procedures items are still requestable through
    // this form, they just get matched with a trained nurse from the
    // Home Nursing team instead of a general attendant (see the
    // clinical-skill validation banner on the requirement form) — so
    // they belong in "what we do", not "what we won't do".
    includes: groupedLabels(PATIENT_CARE_GROUPS),
    excludes: [
      "ICU-level critical care",
      "Surgical procedures of any kind",
      "New IV line or catheter insertion (existing lines/catheters only)",
      "Prescription changes or independent medical advice",
    ],
  },
  {
    id: "cs-9",
    name: "Medical Representative / Medical Care Representative",
    desc: "Trained home nursing support with vitals monitoring and basic clinical assistance.",
    ...CARETAKER_SERVICES["cs-9"],
    image: medical_caretaker_img,
    images: [medical_caretaker_img],
    color: "#ef4444",
    icon: "💉",
    link: "/service/caretaker-service/medical-care-representative",
    // From NURSING_OPTIONS in HomeNursingSupport.js — the exact 9-item
    // checklist the requirement form offers.
    includes: flatLabels(NURSING_OPTIONS),
    excludes: [
      "Inserting new IV lines or catheters",
      "Surgical procedures of any kind",
      "Prescription changes or medical advice",
      "Handling critically ill ICU-level patients",
    ],
  },
  {
    id: "cs-10",
    name: "General Care",
    desc: "Flexible, everyday assistance for care needs that don't fit a specific category — from personal help to companionship.",
    // No fixed price/duration — scope varies too much for a flat rate, so
    // this card shows "Get a Quote" instead (see quote-price handling in
    // the card + modal render below).
    image: caretaker_general_img,
    images: [caretaker_general_img],
    color: "#4f46e5",
    icon: "🤝",
    link: "/service/caretaker-service/general-care",
    // From GENERAL_CARE_GROUPS in GeneralCaretaker.js — all 5 groups
    // (Personal Assistance, Daily Activities, Household Support, Outside
    // Assistance, Companionship), the exact checklist the form offers.
    includes: groupedLabels(GENERAL_CARE_GROUPS),
    excludes: [
      "Clinical or medical procedures of any kind",
      "Full-house deep cleaning beyond the person's own space",
      "Heavy physical labour or repairs",
      "Overnight stay unless booked separately",
    ],
  },
];

const extraServices = [
  { name: "Home Services",      link: "/services/instant-home-services", image: home_services_img },
  { name: "Technical Services", link: "/services/technical-services",    image: technical_services_img },
  { name: "Outdoor Services",   link: "/services/outdoor-services",      image: outdoor_services_img },
  { name: "Transport Services", link: "/services/transport-services",    image: transport_services_img },
  { name: "Special Services",   link: "/services/special-services",      image: special_services_img },
];

// ─── Details Modal ────────────────────────────────────────────────────────────
const DetailsModal = ({ service, onClose, onNavigate }) => {
  const [slide, setSlide] = useState(0);
  const { cityPrice } = useCityPrice();
  if (!service) return null;

  const images = Array.isArray(service.images) && service.images.length
    ? service.images
    : [service.image].filter(Boolean);

  const prev = () => setSlide((s) => (s - 1 + images.length) % images.length);
  const next = () => setSlide((s) => (s + 1) % images.length);

  return (
    <div style={M.overlay} onClick={onClose}>
      <div style={M.card} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={M.header}>
          <div>
            <h2 style={M.title}>{service.name}</h2>
            <div style={M.meta}>
              {service.price != null ? (
                <>
                  <span style={M.price}>₹{cityPrice(service.id, service.price)}</span>
                  <span style={M.sep}>·</span>
                  <span style={M.dur}>{service.duration}</span>
                </>
              ) : (
                <span style={M.price}>Get a Quote</span>
              )}
            </div>
          </div>
          <button style={M.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Image slider */}
        <div style={M.sliderWrap}>
          <img
            src={images[slide]}
            alt={`${service.name} ${slide + 1}`}
            style={M.sliderImg}
            onError={(e) => { e.currentTarget.src = fallbackImage; }}
          />
          {images.length > 1 && (
            <>
              <button style={{ ...M.arrow, left: 10 }} onClick={prev}>‹</button>
              <button style={{ ...M.arrow, right: 10 }} onClick={next}>›</button>
              <div style={M.dots}>
                {images.map((_, i) => (
                  <button
                    key={i}
                    style={{ ...M.dot, ...(i === slide ? M.dotActive : {}) }}
                    onClick={() => setSlide(i)}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Description */}
        {service.desc && <p style={M.desc}>{service.desc}</p>}

        {/* Do / Don't grid */}
        <div style={M.grid}>
          <div style={M.panel}>
            <div style={M.panelHead}>
              <span style={{ ...M.panelBadge, background: "#ecfdf5", color: "#15803d" }}>✓</span>
              <h4 style={M.panelTitle}>What we do</h4>
            </div>
            <ul style={M.list}>
              {(service.includes || []).map((item) => (
                <li key={item} style={M.listItem}>
                  <span style={M.checkIcon}>✔</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div style={M.panel}>
            <div style={M.panelHead}>
              <span style={{ ...M.panelBadge, background: "#fff1f2", color: "#dc2626" }}>✕</span>
              <h4 style={M.panelTitle}>What we won't do</h4>
            </div>
            <ul style={M.list}>
              {(service.excludes || []).map((item) => (
                <li key={item} style={M.listItem}>
                  <span style={{ ...M.checkIcon, color: "#ef4444" }}>✖</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div style={M.footer}>
          <button style={M.closeFooterBtn} onClick={onClose}>Close</button>
          <button style={M.bookBtn} onClick={() => { onClose(); onNavigate(service.link); }}>
            {service.price != null ? "Book Now" : "Get Started"} →
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const CaretakerServices = () => {
  const navigate  = useNavigate();
  const { cityPrice } = useCityPrice();
  const [modalService, setModalService] = useState(null);
  const galleryScrollRef = useRef(null);

  // "See our professionals at work" — single-row horizontal scroller, same
  // pattern as SpecialServices.js's gallery carousel; arrow buttons scroll
  // by one card's width instead of relying on trackpad/touch scrolling.
  const scrollGalleryBy = (direction) => {
    const el = galleryScrollRef.current;
    if (!el) return;
    const card = el.querySelector("[data-gallery-card]");
    const amount = (card?.offsetWidth || 300) + 18;
    el.scrollBy({ left: direction * amount, behavior: "smooth" });
  };

  // Every caretaker service is requirement-based, not a cart SKU — "Book
  // Now" goes straight to that service's requirement form, where
  // Submit Requirement is what actually saves it (to MongoDB). No cart
  // add, no qty/"Booked" state here.
  const handleBookNow = (service) => navigate(service.link);

  // A service the Dashboard's Enable/Disable tab turns off is left out of
  // this list entirely, not shown disabled.
  const visibleServices = services.filter((service) => isServiceEnabled(service.id));

  return (
    <div style={styles.page}>
      <div style={styles.layout}>

        {/* Sidebar */}
        <aside style={styles.sidebar}>
          <CategorySidebar />

          <div style={styles.helpCard}>
            <h4 style={styles.helpTitle}>Need Help Choosing Care Support?</h4>
            <p style={styles.helpText}>Our team will help you select the right caretaker service.</p>
            <button style={styles.chatBtn}>💬 Talk to Support</button>
            <div style={styles.helpIcon}>🧑‍⚕️</div>
          </div>
        </aside>

        {/* Main */}
        <main style={styles.main}>
          <div style={styles.breadcrumb}>Home &gt; Caretaker Services</div>

          {/* Section header */}
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Our Caretaker Services</h2>
            <span style={styles.serviceCount}>Showing {visibleServices.length} services</span>
          </div>

          {/* Service cards */}
          <div style={styles.serviceGrid}>
            {visibleServices.map((service) => (
              <div key={service.id} style={styles.serviceCard}>
                <img
                  src={service.image}
                  alt={service.name}
                  style={styles.serviceImage}
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }}
                />

                <div style={styles.serviceBody}>
                  <div style={{ ...styles.serviceIcon, background: `${service.color}20`, color: service.color }}>
                    ✦
                  </div>
                  <h3 style={styles.serviceName}>{service.name}</h3>
                  <p style={styles.serviceDesc}>{service.desc}</p>

                  <div style={styles.serviceMeta}>
                    {service.price != null ? (
                      <>
                        <span style={styles.price}>₹{cityPrice(service.id, service.price)}</span>
                        <span style={styles.duration}>{service.duration}</span>
                      </>
                    ) : (
                      <span style={styles.quoteLabel}>Get a Quote</span>
                    )}
                  </div>

                  {/* Buttons row */}
                  <div style={styles.btnRow}>
                    <button style={styles.bookBtn} onClick={() => handleBookNow(service)}>
                      {service.price != null ? "Book Now" : "Get Started"}
                    </button>
                    <button
                      style={styles.detailsBtn}
                      onClick={() => setModalService(service)}
                    >
                      Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* See our professionals at work */}
          <h3 style={styles.gallerySectionTitle}>See our professionals at work</h3>
          <div style={styles.galleryCarouselWrap}>
            <button type="button" style={{ ...styles.galleryArrow, left: "-18px" }} onClick={() => scrollGalleryBy(-1)} aria-label="Previous">
              ‹
            </button>
            <div style={styles.galleryGrid} ref={galleryScrollRef}>
              <div style={styles.galleryCard} data-gallery-card>
                <img src={elder_caretaker_169_img} alt="Elder care support" style={styles.galleryImage}
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }} />
                <div style={styles.galleryCaption}>Elder care support</div>
              </div>
              <div style={styles.galleryCard} data-gallery-card>
                <img src={baby_caretaker_169_img} alt="Baby care assistance" style={styles.galleryImage}
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }} />
                <div style={styles.galleryCaption}>Baby care assistance</div>
              </div>
              <div style={styles.galleryCard} data-gallery-card>
                <img src={patient_caretaker_169_img} alt="Patient care support" style={styles.galleryImage}
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }} />
                <div style={styles.galleryCaption}>Patient care support</div>
              </div>
            </div>
            <button type="button" style={{ ...styles.galleryArrow, right: "-18px" }} onClick={() => scrollGalleryBy(1)} aria-label="Next">
              ›
            </button>
          </div>

          {/* You may also need */}
          <div style={styles.exploreSection}>
            <h3 style={styles.exploreTitle}>You may also need</h3>
            <div style={styles.exploreGrid}>
              {extraServices.map((item, index) => (
                <Link key={index} to={item.link} style={styles.exploreCardLink}>
                  <div style={styles.exploreCard}>
                    <img
                      src={item.image}
                      alt={item.name}
                      style={styles.exploreImage}
                      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }}
                    />
                    <div style={styles.exploreCardBody}>
                      <h4 style={styles.exploreName}>{item.name}</h4>
                      <span style={styles.exploreLink}>Check Now →</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Banner */}
          <div style={styles.bannerCard}>
            <img
              src="https://images.unsplash.com/photo-1516589091380-5d8e87df6999?auto=format&fit=crop&w=600&q=80"
              alt="care support plan"
              style={styles.bannerImage}
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }}
            />
            <div style={styles.bannerContent}>
              <h3 style={styles.bannerTitle}>Complete Daily Care Support</h3>
              <p style={styles.bannerText}>Personal care, daily assistance and compassionate companionship</p>
              <div style={styles.bannerTags}>
                <span>Baby Care</span><span>Elder Care</span><span>Patient Support</span><span>Night Help</span>
              </div>
            </div>
            <div style={styles.bannerAction}>
              <p style={styles.saveText}>Packages from ₹1499</p>
              <button style={styles.bookNowBtn}>Book Now →</button>
            </div>
          </div>
        </main>
      </div>

      {/* Details Modal */}
      <DetailsModal
        service={modalService}
        onClose={() => setModalService(null)}
        onNavigate={navigate}
      />
    </div>
  );
};

// ─── Page styles ──────────────────────────────────────────────────────────────
const styles = {
  page:             { background: "#f8fafc", minHeight: "100vh", padding: "20px", fontFamily: "Arial, sans-serif", color: "#111827" },
  layout:           { maxWidth: "1300px", margin: "0 auto", display: "grid", gridTemplateColumns: "250px 1fr", gap: "20px", alignItems: "start" },
  sidebar:          { display: "flex", flexDirection: "column", gap: "18px" },
  helpCard:         { background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: "18px", padding: "18px", textAlign: "left" },
  helpTitle:        { margin: "0 0 8px", fontSize: "18px", fontWeight: 700 },
  helpText:         { margin: "0 0 14px", fontSize: "14px", color: "#6b7280", lineHeight: 1.6 },
  chatBtn:          { border: "none", background: "#fff", color: "#4f46e5", fontWeight: 700, borderRadius: "12px", padding: "12px 16px", cursor: "pointer" },
  helpIcon:         { fontSize: "54px", textAlign: "center", marginTop: "18px" },
  main:             { minWidth: 0 },
  breadcrumb:       { fontSize: "13px", color: "#9ca3af", marginBottom: "14px" },
  sectionHeader:    { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" },
  sectionTitle:     { margin: 0, fontSize: "24px", fontWeight: 700 },
  serviceCount:     { fontSize: "14px", color: "#6b7280" },
  serviceGrid:      { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "14px" },
  serviceCard:      { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "14px", overflow: "hidden", position: "relative" },
  serviceImage:     { width: "100%", aspectRatio: "4 / 3", objectFit: "cover" },
  serviceBody:      { padding: "12px" },
  serviceIcon:      { width: "26px", height: "26px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "8px", fontSize: "13px", fontWeight: 700 },
  serviceName:      { margin: "0 0 6px", fontSize: "15px", fontWeight: 700 },
  serviceDesc:      { margin: "0 0 10px", fontSize: "12px", lineHeight: 1.5, color: "#6b7280", minHeight: "48px" },
  serviceMeta:      { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" },
  price:            { fontSize: "17px", fontWeight: 800 },
  duration:         { fontSize: "11.5px", color: "#6b7280", fontWeight: 600 },
  quoteLabel:       { fontSize: "13px", fontWeight: 700, color: "#4f46e5" },
  btnRow:           { display: "grid", gridTemplateColumns: "1fr auto", gap: "6px" },
  bookBtn:          { height: "36px", border: "none", background: "linear-gradient(135deg,#4f46e5,#4338ca)", color: "#fff", borderRadius: "10px", fontWeight: 700, fontSize: "12.5px", cursor: "pointer" },
  detailsBtn:       { height: "36px", padding: "0 10px", border: "1px solid #e2e8f0", background: "#f8fafc", color: "#374151", borderRadius: "10px", fontWeight: 700, fontSize: "12.5px", cursor: "pointer", whiteSpace: "nowrap" },
  gallerySectionTitle: { margin: "32px 0 16px", fontSize: "24px", fontWeight: 800 },
  galleryCarouselWrap: { position: "relative", padding: "0 6px" },
  galleryGrid: {
    display: "flex",
    gap: "18px",
    overflowX: "auto",
    scrollSnapType: "x mandatory",
    scrollBehavior: "smooth",
    paddingBottom: "4px",
  },
  galleryCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "18px",
    overflow: "hidden",
    flex: "0 0 320px",
    width: "320px",
    scrollSnapAlign: "start",
  },
  galleryImage:     { width: "100%", aspectRatio: "16 / 9", objectFit: "cover", display: "block" },
  galleryCaption:   { padding: "14px 16px", fontWeight: 700, fontSize: "15px" },
  galleryArrow: {
    position: "absolute",
    top: "calc(50% - 20px)",
    transform: "translateY(-50%)",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#111827",
    fontSize: "20px",
    fontWeight: 700,
    lineHeight: 1,
    cursor: "pointer",
    boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
  },
  bannerCard:       { marginTop: "22px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "16px", display: "grid", gridTemplateColumns: "180px 1fr auto", gap: "16px", alignItems: "center" },
  bannerImage:      { width: "100%", aspectRatio: "16 / 9", objectFit: "cover", borderRadius: "14px" },
  bannerContent:    {},
  bannerTitle:      { margin: "0 0 6px", fontSize: "20px", fontWeight: 700, color: "#4f46e5" },
  bannerText:       { margin: "0 0 10px", fontSize: "14px", color: "#6b7280" },
  bannerTags:       { display: "flex", gap: "10px", flexWrap: "wrap", fontSize: "13px", color: "#4b5563" },
  bannerAction:     { textAlign: "right" },
  saveText:         { margin: "0 0 10px", fontSize: "13px", color: "#9ca3af" },
  bookNowBtn:       { border: "none", background: "#4f46e5", color: "#fff", height: "44px", padding: "0 18px", borderRadius: "12px", fontWeight: 700, cursor: "pointer" },
  exploreSection:   { marginTop: "32px" },
  exploreTitle:     { margin: "0 0 14px", fontSize: "24px", fontWeight: 700 },
  exploreGrid:      { display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: "12px" },
  exploreCardLink:  { textDecoration: "none", color: "inherit" },
  exploreCard:      { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", overflow: "hidden", textAlign: "left" },
  exploreImage:     { width: "100%", aspectRatio: "1 / 1", objectFit: "cover", display: "block", background: "#f3f4f6" },
  exploreCardBody:  { padding: "14px" },
  exploreName:      { margin: "0 0 10px", fontSize: "15px", fontWeight: 700 },
  exploreLink:      { color: "#4f46e5", fontWeight: 700, fontSize: "13px" },
};

// ─── Modal styles ─────────────────────────────────────────────────────────────
const M = {
  overlay:       { position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px", zIndex: 9999 },
  card:          { width: "min(860px,100%)", maxHeight: "90vh", overflowY: "auto", background: "#fff", borderRadius: "22px", border: "1px solid #e2e8f0", boxShadow: "0 24px 60px rgba(15,23,42,0.2)", display: "flex", flexDirection: "column" },
  header:        { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", padding: "20px 22px 14px", borderBottom: "1px solid #eef2ff" },
  title:         { margin: "0 0 6px", fontSize: "24px", fontWeight: 800, color: "#111827" },
  meta:          { display: "flex", alignItems: "center", gap: "8px" },
  price:         { fontSize: "20px", fontWeight: 800, color: "#4f46e5" },
  sep:           { color: "#94a3b8" },
  dur:           { fontSize: "14px", color: "#64748b", fontWeight: 600 },
  closeBtn:      { width: "36px", height: "36px", borderRadius: "10px", border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", fontSize: "14px", fontWeight: 800, flexShrink: 0 },
  sliderWrap:    { position: "relative", width: "100%", height: "280px", background: "#f1f5f9", overflow: "hidden", flexShrink: 0 },
  sliderImg:     { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  arrow:         { position: "absolute", top: "50%", transform: "translateY(-50%)", width: "36px", height: "36px", borderRadius: "50%", border: "none", background: "rgba(0,0,0,0.45)", color: "#fff", fontSize: "22px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, lineHeight: 1, zIndex: 2 },
  dots:          { position: "absolute", bottom: "10px", left: "50%", transform: "translateX(-50%)", display: "flex", gap: "6px", zIndex: 2 },
  dot:           { width: "8px", height: "8px", borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.55)", cursor: "pointer", padding: 0 },
  dotActive:     { background: "#fff" },
  desc:          { padding: "14px 22px 0", fontSize: "14px", color: "#64748b", lineHeight: 1.7, margin: 0 },
  grid:          { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", padding: "14px 22px" },
  panel:         { border: "1px solid #e2e8f0", borderRadius: "14px", padding: "16px", background: "#fbfcff" },
  panelHead:     { display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" },
  panelBadge:    { width: "24px", height: "24px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 800, flexShrink: 0 },
  panelTitle:    { margin: 0, fontSize: "15px", fontWeight: 800, color: "#111827" },
  list:          { margin: 0, padding: 0, listStyle: "none", display: "grid", gap: "10px" },
  listItem:      { display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "13px", color: "#374151", lineHeight: 1.6 },
  checkIcon:     { fontSize: "12px", color: "#16a34a", marginTop: "2px", flexShrink: 0 },
  footer:        { padding: "14px 22px 20px", borderTop: "1px solid #eef2ff", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "12px" },
  closeFooterBtn:{ height: "44px", padding: "0 20px", border: "1px solid #e2e8f0", background: "#f8fafc", color: "#374151", borderRadius: "12px", fontWeight: 700, cursor: "pointer" },
  bookBtn:       { height: "44px", padding: "0 24px", border: "none", background: "linear-gradient(135deg,#4f46e5,#4338ca)", color: "#fff", borderRadius: "12px", fontWeight: 700, cursor: "pointer", fontSize: "14px" },
};

export default CaretakerServices;
